/**
 * API клиент для образовательных коллекций
 */
import {
  CollectionsListResponse,
  CollectionDetailResponse,
  CollectionProgressResponse,
  ModuleStartResponse,
  ModuleCompleteResponse,
  CollectionAccessResponse,
  CollectionPurchaseResponse,
  ModuleAnswer,
} from '@beshek/shared';

const API_BASE = '/api';

// Получить заголовки авторизации (как в swipe-api)
import { getAuthHeaders } from "./auth-headers";

// Базовый fetch с headers
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function getCollections(filters?: {
  category?: string;
  difficulty?: string;
  access?: 'free' | 'premium' | 'all';
}): Promise<CollectionsListResponse> {
  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  if (filters?.difficulty) params.set('difficulty', filters.difficulty);
  if (filters?.access) params.set('access', filters.access);

  const query = params.toString();
  return fetchApi<CollectionsListResponse>(
    `/collections${query ? `?${query}` : ''}`
  );
}

export async function getCollectionCategories(): Promise<{
  categories: Array<{ id: string; name: string; count: number }>;
}> {
  return fetchApi('/collections/categories');
}

export async function getCollectionDetail(
  collectionId: string
): Promise<CollectionDetailResponse> {
  return fetchApi<CollectionDetailResponse>(`/collections/${collectionId}`);
}

export async function getCollectionProgress(
  collectionId: string
): Promise<CollectionProgressResponse> {
  return fetchApi<CollectionProgressResponse>(
    `/collections/${collectionId}/progress`
  );
}

export async function startModule(
  collectionId: string,
  moduleId: string
): Promise<ModuleStartResponse> {
  return fetchApi<ModuleStartResponse>(
    `/collections/${collectionId}/modules/${moduleId}/start`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export async function completeModule(
  collectionId: string,
  moduleId: string,
  sessionId: string,
  answers: (ModuleAnswer & { value?: string; assignment?: Record<string, string>; order?: string[]; lineId?: string; points?: { x: number; y: number }[] })[]
): Promise<ModuleCompleteResponse> {
  return fetchApi<ModuleCompleteResponse>(
    `/collections/${collectionId}/modules/${moduleId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify({ sessionId, answers }),
    }
  );
}

export async function checkCollectionAccess(
  collectionId: string
): Promise<CollectionAccessResponse> {
  return fetchApi<CollectionAccessResponse>(
    `/collections/${collectionId}/access`
  );
}

export async function purchaseCollection(
  collectionId: string,
  paymentId: string
): Promise<CollectionPurchaseResponse> {
  return fetchApi<CollectionPurchaseResponse>(
    `/collections/${collectionId}/purchase`,
    {
      method: 'POST',
      body: JSON.stringify({ paymentId }),
    }
  );
}

/** Зона на картинке (клик по фото): координаты в % от ширины/высоты. */
export interface LineOption { id: string; points: number[]; correct: boolean; label?: string; explanation?: string }
export interface HotspotZone { id: string; shape: 'rect' | 'poly'; coords: number[]; correct?: boolean; label?: string; explanation?: string }

export async function answerQuestion(
  collectionId: string,
  moduleId: string,
  sessionId: string,
  questionId: string,
  answerIndex: number,
  point?: { x: number; y: number },
  placements?: Record<string, string>,
  order?: string[],
  points?: { x: number; y: number }[],
  lineId?: string,
  assignment?: Record<string, string>,
  value?: string
): Promise<{ correctAnswer?: string; perItem?: Record<string, boolean>; items?: { id: string; text: string; category: string }[]; lines?: LineOption[]; chosen?: string; correct: boolean; correctIndex: number; explanation?: string; hit?: string | null; hits?: Record<string, boolean>; missed?: string[]; stray?: number; zones?: HotspotZone[]; perZone?: Record<string, boolean>; correctOrder?: string[]; perStep?: Record<string, boolean>; steps?: { id: string; text: string }[] }> {
  return fetchApi(
    `/collections/${collectionId}/modules/${moduleId}/answer`,
    {
      method: 'POST',
      body: JSON.stringify({ sessionId, questionId, answerIndex, point, placements, order, points, lineId, assignment, value }),
    }
  );
}
